import { useState, useEffect, useCallback } from 'react';
import Layout from '@/components/Layout';
import { useAuth } from '@/lib/authContext';
import { supabase } from '@/lib/supabase';
import toast from 'react-hot-toast';

interface SyncRun {
  id: string;
  sync_type: string;
  status: string;
  started_at: string;
  completed_at: string | null;
  records_processed: number;
  error_message: string | null;
  metadata: any;
}

interface CompanyMapping {
  id: string;
  cliente_id: string;
  cegid_company_code: string;
  api_type: string;
  tipo_opcion_fiscal: number;
  active: boolean;
  sync_enabled: boolean;
  clientes?: { razon_social: string };
}

export default function SyncCegid() {
  const { user } = useAuth();
  const [syncRuns, setSyncRuns] = useState<SyncRun[]>([]);
  const [mappings, setMappings] = useState<CompanyMapping[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [internalSyncing, setInternalSyncing] = useState(false);
  const [activeTab, setActiveTab] = useState<'status' | 'config'>('status');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [runsRes, mappingsRes] = await Promise.all([
        supabase
          .from('cegid_sync_runs')
          .select('*')
          .order('started_at', { ascending: false })
          .limit(50),
        supabase
          .from('cegid_company_mapping')
          .select('*, clientes(razon_social)')
          .order('cegid_company_code', { ascending: true }),
      ]);

      if (runsRes.data) setSyncRuns(runsRes.data);
      if (mappingsRes.data) setMappings(mappingsRes.data as any);
    } catch (err) {
      toast.error('Error cargando datos de sincronización');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const triggerSync = async (companyCode?: string) => {
    const enabledMappings = companyCode
      ? mappings.filter(m => m.cegid_company_code === companyCode)
      : mappings.filter(m => m.sync_enabled && m.active);

    if (enabledMappings.length === 0) {
      toast.error('No hay empresas habilitadas para sincronizar. Active al menos una en Configuración.');
      return;
    }

    setSyncing(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        toast.error('Sesión expirada. Por favor, vuelve a iniciar sesión.');
        return;
      }

      let successCount = 0;
      let failCount = 0;

      for (const m of enabledMappings) {
        try {
          const res = await fetch('/api/cegid/sync', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({ companyCode: m.cegid_company_code }),
          });
          const data = await res.json();
          if (res.ok) {
            successCount++;
            toast.success(`Sync OK: ${m.cegid_company_code} (${data.recordsProcessed || 0} registros)`);
          } else {
            failCount++;
            toast.error(`${m.cegid_company_code}: ${data.error || 'Error'}`);
          }
        } catch {
          failCount++;
          toast.error(`${m.cegid_company_code}: Error de conexión`);
        }
      }

      if (successCount > 0) {
        setTimeout(loadData, 2000);
      }
    } catch (err) {
      toast.error('Error de conexión');
    } finally {
      setSyncing(false);
    }
  };

  const triggerInternalSync = async () => {
    setInternalSyncing(true);
    try {
      const res = await fetch('/api/dashboard/internal-sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const data = await res.json();

      if (res.ok) {
        toast.success(`Sincronización interna completada: ${data.recordsProcessed || 0} registros procesados`);
        setTimeout(loadData, 2000);
      } else {
        toast.error(`Error: ${data.error || 'Error desconocido'}`);
      }
    } catch (err) {
      toast.error('Error de conexión con el servidor');
    } finally {
      setInternalSyncing(false);
    }
  };

  const toggleSyncEnabled = async (mapping: CompanyMapping) => {
    try {
      const { error } = await supabase
        .from('cegid_company_mapping')
        .update({ sync_enabled: !mapping.sync_enabled })
        .eq('id', mapping.id);

      if (error) throw error;
      toast.success(`Sync ${!mapping.sync_enabled ? 'activado' : 'desactivado'} para ${mapping.cegid_company_code}`);
      loadData();
    } catch (err) {
      toast.error('Error actualizando configuración');
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <span className="material-symbols-outlined text-base text-green-500">check_circle</span>;
      case 'failed': return <span className="material-symbols-outlined text-base text-red-500">cancel</span>;
      case 'running': return <span className="material-symbols-outlined text-base text-blue-500 animate-spin">sync</span>;
      default: return <span className="material-symbols-outlined text-base text-calm-muted">schedule</span>;
    }
  };

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      completed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      running: 'bg-blue-100 text-blue-800',
    };
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${colors[status] || 'bg-calm-bg text-calm-text'}`}>
        {getStatusIcon(status)}
        {status}
      </span>
    );
  };

  const stats = {
    total: syncRuns.length,
    completed: syncRuns.filter(r => r.status === 'completed').length,
    failed: syncRuns.filter(r => r.status === 'failed').length,
    running: syncRuns.filter(r => r.status === 'running').length,
    lastSync: syncRuns[0] || null,
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-5xl font-light font-display text-calm-text">Sincronización Cegid</h1>
            <p className="text-sm text-calm-muted mt-1">Gestión y monitoreo de la sincronización con Cegid ERP</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={loadData}
              disabled={loading}
              className="ghost-btn flex items-center gap-2 px-3 py-2 text-sm"
            >
              <span className={`material-symbols-outlined text-base ${loading ? 'animate-spin' : ''}`}>refresh</span>
              Actualizar
            </button>
            <button
              onClick={() => triggerSync()}
              disabled={syncing}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-calm-accent text-white rounded-huge hover:bg-calm-accent/90 disabled:opacity-50"
            >
              <span className="material-symbols-outlined text-base">play_arrow</span>
              {syncing ? 'Sincronizando...' : 'Sync Manual'}
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge p-4">
            <p className="text-sm text-calm-muted">Total Syncs (últimos 50)</p>
            <p className="text-2xl font-light">{stats.total}</p>
          </div>
          <div className="bg-gradient-to-br from-white to-emerald-50/20 border border-emerald-200/40 rounded-huge p-4">
            <p className="text-sm text-calm-muted">Completados</p>
            <p className="text-2xl font-light text-green-600">{stats.completed}</p>
          </div>
          <div className="bg-gradient-to-br from-white to-amber-50/20 border border-amber-200/40 rounded-huge p-4">
            <p className="text-sm text-calm-muted">Fallidos</p>
            <p className="text-2xl font-light text-red-600">{stats.failed}</p>
          </div>
          <div className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge p-4">
            <p className="text-sm text-calm-muted">Último Sync</p>
            <p className="text-sm font-medium">
              {stats.lastSync
                ? new Date(stats.lastSync.started_at).toLocaleString('es-ES')
                : 'Nunca'}
            </p>
            {stats.lastSync && getStatusBadge(stats.lastSync.status)}
          </div>
        </div>

        {/* Internal Sync Card */}
        <div className="mb-6 bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge p-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h2 className="text-lg font-medium text-calm-text mb-1">Sincronización Interna</h2>
              <p className="text-sm text-calm-muted mb-4">
                Recalcula los datos financieros y fiscales a partir de los documentos contabilizados en el sistema. Útil cuando la sincronización con Cegid no está configurada.
              </p>
              <button
                onClick={triggerInternalSync}
                disabled={internalSyncing}
                className="flex items-center gap-2 px-4 py-2 text-sm bg-calm-accent text-white rounded-huge hover:bg-calm-accent/90 disabled:opacity-50 transition-all"
              >
                <span className={`material-symbols-outlined text-base ${internalSyncing ? 'animate-spin' : ''}`}>
                  {internalSyncing ? 'sync' : 'calculate'}
                </span>
                {internalSyncing ? 'Recalculando...' : 'Recalcular Datos'}
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-calm-border mb-4">
          <nav className="flex gap-4">
            <button
              onClick={() => setActiveTab('status')}
              className={`pb-2 px-1 text-sm font-medium border-b-2 ${
                activeTab === 'status'
                  ? 'border-calm-accent text-calm-accent'
                  : 'border-transparent text-calm-muted hover:text-calm-text'
              }`}
            >
              <span className="material-symbols-outlined text-base inline mr-1">sync</span>
              Historial de Syncs
            </button>
            <button
              onClick={() => setActiveTab('config')}
              className={`pb-2 px-1 text-sm font-medium border-b-2 ${
                activeTab === 'config'
                  ? 'border-calm-accent text-calm-accent'
                  : 'border-transparent text-calm-muted hover:text-calm-text'
              }`}
            >
              <span className="material-symbols-outlined text-base inline mr-1">settings</span>
              Configuración Empresas
            </button>
          </nav>
        </div>

        {/* Tab Content: Sync History */}
        {activeTab === 'status' && (
          <div className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge overflow-hidden">
            <table className="min-w-full divide-y divide-calm-border">
              <thead className="bg-gradient-to-r from-steel-azure-50/50 to-fresh-sky-50/30">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-calm-muted uppercase">Estado</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-calm-muted uppercase">Tipo</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-calm-muted uppercase">Inicio</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-calm-muted uppercase">Fin</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-calm-muted uppercase">Registros</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-calm-muted uppercase">Error</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-calm-border">
                {syncRuns.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-calm-muted">
                      No hay registros de sincronización aún.
                    </td>
                  </tr>
                )}
                {syncRuns.map((run) => (
                  <tr key={run.id} className="hover:bg-calm-bg">
                    <td className="px-4 py-3">{getStatusBadge(run.status)}</td>
                    <td className="px-4 py-3 text-sm text-calm-text">{run.sync_type}</td>
                    <td className="px-4 py-3 text-sm text-calm-muted">
                      {new Date(run.started_at).toLocaleString('es-ES')}
                    </td>
                    <td className="px-4 py-3 text-sm text-calm-muted">
                      {run.completed_at ? new Date(run.completed_at).toLocaleString('es-ES') : '—'}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium">{run.records_processed || 0}</td>
                    <td className="px-4 py-3 text-sm text-red-600 max-w-xs truncate">
                      {run.error_message || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Tab Content: Company Mapping Config */}
        {activeTab === 'config' && (
          <div className="space-y-4">
            {mappings.length === 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-huge p-4 flex items-start gap-3">
                <span className="material-symbols-outlined text-xl text-yellow-600 mt-0.5">warning</span>
                <div>
                  <p className="text-sm font-medium text-yellow-800">Sin empresas configuradas</p>
                  <p className="text-sm text-yellow-600 mt-1">
                    Añada mappings en la tabla <code>cegid_company_mapping</code> para configurar la sincronización.
                  </p>
                </div>
              </div>
            )}
            {mappings.map((m) => (
              <div key={m.id} className="bg-gradient-to-br from-white to-steel-azure-50/20 border border-steel-azure-200/40 rounded-huge p-4 flex items-center justify-between">
                <div>
                  <p className="font-medium text-calm-text">
                    {m.clientes?.razon_social || m.cliente_id}
                  </p>
                  <div className="flex gap-3 mt-1 text-sm text-calm-muted">
                    <span>Código: <strong>{m.cegid_company_code}</strong></span>
                    <span>API: <strong>{m.api_type.toUpperCase()}</strong></span>
                    <span>Opción Fiscal: <strong>{m.tipo_opcion_fiscal}</strong></span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-xs font-medium ${m.active ? 'text-green-600' : 'text-calm-muted'}`}>
                    {m.active ? 'Activo' : 'Inactivo'}
                  </span>
                  <button
                    onClick={() => toggleSyncEnabled(m)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      m.sync_enabled ? 'bg-calm-accent' : 'bg-calm-border'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        m.sync_enabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                  <span className="text-xs text-calm-muted w-16">
                    {m.sync_enabled ? 'Sync ON' : 'Sync OFF'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
